import { useRichTextCopy } from "../i18n/richTextCatalog";
import { formatActiveNumber } from "../i18n/formatting";
import { useMemo, useRef, useState, type ReactNode, type TextareaHTMLAttributes } from "react";
import {
  Bold,
  ExternalLink,
  Italic,
  Link2,
  List,
  ListOrdered,
  Underline,
  X,
} from "lucide-react";

import "../styles/safe-rich-text.css";

type EditorProps = {
  name: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  maxLength?: number | null | undefined;
  rows?: number;
  ariaLabel?: string | undefined;
  textareaRef?: React.Ref<HTMLTextAreaElement>;
  textareaProps?: TextareaHTMLAttributes<HTMLTextAreaElement>;
};

type InlineNode = ReactNode;
const VARIABLE_HREF = /^{{[a-zA-Z][a-zA-Z0-9_]*}}$/;

function allowedHref(raw: string): string | null {
  const value = raw.trim();
  if (VARIABLE_HREF.test(value)) return value;
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function inline(text: string): InlineNode[] {
  const token = /(\[[^\]\n]+\]\((?:(?:https?:\/\/|\/)[^)\s]+|{{[a-zA-Z][a-zA-Z0-9_]*}})\)|\*\*[^*\n]+\*\*|\+\+[^+\n]+\+\+|\*[^*\n]+\*)/g;
  const result: InlineNode[] = [];
  let cursor = 0;
  let index = 0;
  for (const match of text.matchAll(token)) {
    const start = match.index ?? 0;
    if (start > cursor) result.push(text.slice(cursor, start));
    const value = match[0];
    if (value.startsWith("**")) {
      result.push(<strong key={`b-${index}`}>{value.slice(2, -2)}</strong>);
    } else if (value.startsWith("++")) {
      result.push(<u key={`u-${index}`}>{value.slice(2, -2)}</u>);
    } else if (value.startsWith("*")) {
      result.push(<em key={`i-${index}`}>{value.slice(1, -1)}</em>);
    } else {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(value);
      const href = linkMatch ? allowedHref(linkMatch[2] ?? "") : null;
      if (linkMatch && href) {
        if (VARIABLE_HREF.test(href)) {
          result.push(<span key={`template-link-${index}`} className="safe-rich-template-link"><Link2 size={11} />{linkMatch[1]} · {href}</span>);
        } else {
          const external = /^https?:\/\//.test(href);
          result.push(<a key={`a-${index}`} href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer noopener" : undefined}>{linkMatch[1]}{external && <ExternalLink size={11} />}</a>);
        }
      } else result.push(value);
    }
    cursor = start + value.length;
    index += 1;
  }
  if (cursor < text.length) result.push(text.slice(cursor));
  return result;
}

export function SafeInlineText({ value }: { value: string }) { return <>{inline(value)}</>; }

export function SafeRichTextPreview({ value, empty }: { value: string; empty?: string }) {
  const c = useRichTextCopy();
  const blocks = useMemo(() => {
    const lines = value.replaceAll("\r\n", "\n").split("\n");
    const output: ReactNode[] = [];
    let index = 0;
    while (index < lines.length) {
      const line = lines[index] ?? "";
      if (!line.trim()) { index += 1; continue; }
      if (/^\s*-\s+/.test(line)) {
        const items: string[] = [];
        while (index < lines.length && /^\s*-\s+/.test(lines[index] ?? "")) {
          items.push((lines[index] ?? "").replace(/^\s*-\s+/, ""));
          index += 1;
        }
        output.push(<ul key={`ul-${index}`}>{items.map((item, itemIndex) => <li key={itemIndex}>{inline(item)}</li>)}</ul>);
        continue;
      }
      if (/^\s*\d+\.\s+/.test(line)) {
        const items: string[] = [];
        while (index < lines.length && /^\s*\d+\.\s+/.test(lines[index] ?? "")) {
          items.push((lines[index] ?? "").replace(/^\s*\d+\.\s+/, ""));
          index += 1;
        }
        output.push(<ol key={`ol-${index}`}>{items.map((item, itemIndex) => <li key={itemIndex}>{inline(item)}</li>)}</ol>);
        continue;
      }
      output.push(<p key={`p-${index}`}>{inline(line)}</p>);
      index += 1;
    }
    return output;
  }, [value]);

  return <div className="safe-rich-preview">{blocks.length ? blocks : <p className="is-empty">{empty ?? c("previewEmpty")}</p>}</div>;
}

export function SafeRichTextEditor({ name, value, onChange, placeholder, required, maxLength = 50_000, rows = 15, ariaLabel, textareaRef, textareaProps }: EditorProps) {
  const c = useRichTextCopy();
  const textarea = useRef<HTMLTextAreaElement>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);
  const selection = useRef({ start: 0, end: 0, text: "" });

  function replaceSelection(prefix: string, suffix = prefix, fallback = c("text")) {
    const element = textarea.current;
    if (!element) return;
    const start = element.selectionStart ?? value.length;
    const end = element.selectionEnd ?? start;
    const selected = value.slice(start, end) || fallback;
    const next = `${value.slice(0, start)}${prefix}${selected}${suffix}${value.slice(end)}`;
    onChange(next.slice(0, maxLength ?? undefined));
    window.requestAnimationFrame(() => {
      element.focus();
      const caret = start + prefix.length + selected.length + suffix.length;
      element.setSelectionRange(caret, caret);
    });
  }

  function prefixLines(prefix: string, numbered = false) {
    const element = textarea.current;
    if (!element) return;
    const start = element.selectionStart ?? 0;
    const end = element.selectionEnd ?? start;
    const before = value.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
    const afterIndex = value.indexOf("\n", end);
    const after = afterIndex === -1 ? value.length : afterIndex;
    const source = value.slice(before, after) || c("listItem");
    const formatted = source.split("\n").map((line, index) => `${numbered ? `${index + 1}. ` : prefix}${line.replace(/^\s*(?:-\s+|\d+\.\s+)/, "")}`).join("\n");
    const next = `${value.slice(0, before)}${formatted}${value.slice(after)}`;
    onChange(next.slice(0, maxLength ?? undefined));
    window.requestAnimationFrame(() => { element.focus(); element.setSelectionRange(before, before + formatted.length); });
  }

  function beginLink() {
    const element = textarea.current;
    if (!element) return;
    const start = element.selectionStart ?? value.length;
    const end = element.selectionEnd ?? start;
    selection.current = { start, end, text: value.slice(start, end) || c("linkText") };
    setLinkUrl("");
    setLinkError(null);
    setLinkOpen(true);
  }

  function applyLink() {
    const href = allowedHref(linkUrl);
    if (!href) { setLinkError(c("linkError")); return; }
    const { start, end, text } = selection.current;
    const markup = `[${text}](${href})`;
    onChange(`${value.slice(0, start)}${markup}${value.slice(end)}`.slice(0, maxLength ?? undefined));
    setLinkOpen(false);
    setLinkUrl("");
    window.requestAnimationFrame(() => textarea.current?.focus());
  }

  return <div className={`safe-rich-editor${textareaProps ? " is-native-field" : ""}`}>
    <div className="safe-rich-toolbar" role="toolbar" aria-label={c("formatting")}>
      <button type="button" disabled={textareaProps?.disabled || textareaProps?.readOnly} onMouseDown={(event) => event.preventDefault()} title={c("bold")} aria-label={c("bold")} onClick={() => replaceSelection("**", "**", c("bold"))}><Bold size={16} /></button>
      <button type="button" disabled={textareaProps?.disabled || textareaProps?.readOnly} onMouseDown={(event) => event.preventDefault()} title={c("italic")} aria-label={c("italic")} onClick={() => replaceSelection("*", "*", c("italic"))}><Italic size={16} /></button>
      <button type="button" disabled={textareaProps?.disabled || textareaProps?.readOnly} onMouseDown={(event) => event.preventDefault()} title={c("underline")} aria-label={c("underline")} onClick={() => replaceSelection("++", "++", c("underline"))}><Underline size={16} /></button>
      <span />
      <button type="button" disabled={textareaProps?.disabled || textareaProps?.readOnly} onMouseDown={(event) => event.preventDefault()} title={c("bullets")} aria-label={c("bullets")} onClick={() => prefixLines("- ")}><List size={16} /></button>
      <button type="button" disabled={textareaProps?.disabled || textareaProps?.readOnly} onMouseDown={(event) => event.preventDefault()} title={c("numbers")} aria-label={c("numbers")} onClick={() => prefixLines("", true)}><ListOrdered size={16} /></button>
      <span />
      <button type="button" disabled={textareaProps?.disabled || textareaProps?.readOnly} onMouseDown={(event) => event.preventDefault()} title={c("insertLink")} aria-label={c("insertLink")} onClick={beginLink}><Link2 size={16} /></button>
      <small>{c("safeFormatting")}</small>
    </div>
    {linkOpen && <div className="safe-rich-link-editor">
      <Link2 size={16} />
      <label><span>{c("url")}</span><input autoFocus value={linkUrl} onChange={(event) => { setLinkUrl(event.currentTarget.value); setLinkError(null); }} placeholder={c("url")} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); applyLink(); } if (event.key === "Escape") setLinkOpen(false); }} /></label>
      <button type="button" disabled={textareaProps?.disabled || textareaProps?.readOnly} onMouseDown={(event) => event.preventDefault()} className="apply" aria-label={c("confirmLink")} onClick={applyLink}>{c("insertLink")}</button>
      <button type="button" disabled={textareaProps?.disabled || textareaProps?.readOnly} onMouseDown={(event) => event.preventDefault()} aria-label={c("cancelLink")} onClick={() => setLinkOpen(false)}><X size={15} /></button>
      {linkError && <p role="alert">{linkError}</p>}
    </div>}
    <textarea {...textareaProps} ref={(element) => { textarea.current = element; if (typeof textareaRef === "function") textareaRef(element); else if (textareaRef) (textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = element; }} aria-label={ariaLabel ?? textareaProps?.["aria-label"] ?? (textareaProps ? undefined : c("body"))} name={name} value={value} onChange={(event) => { onChange(event.currentTarget.value); textareaProps?.onChange?.(event); }} onKeyDown={(event) => { textareaProps?.onKeyDown?.(event); if (event.defaultPrevented || textareaProps?.disabled || textareaProps?.readOnly || !(event.ctrlKey || event.metaKey)) return; const key = event.key.toLowerCase(); if (!["b", "i", "u"].includes(key)) return; event.preventDefault(); const marker = key === "b" ? "**" : key === "i" ? "*" : "++"; replaceSelection(marker); }} rows={rows} maxLength={maxLength ?? undefined} required={required} placeholder={placeholder} />
    <div className="safe-rich-format-help"><span><strong>{c("formatting")}</strong> <code>**bold**</code> <code>*italic*</code> <code>++underline++</code> <code>- bullet</code> <code>1. numbered</code> <code>[label](https://…)</code></span><small>{maxLength === null ? formatActiveNumber(value.length) : c("characters", { count: formatActiveNumber(value.length), limit: formatActiveNumber(maxLength ?? 0) })}</small></div>
  </div>;
}
